/**
 * Where the chat socket connects, and in what order the answers are tried.
 *
 * Extracted from `ChatContext` so the precedence can be tested. In there it
 * ran at module scope beside `io(...)`, which meant importing it to test it
 * also opened a socket.
 *
 * MYD-458.
 */

export interface ChatServerUrlSources {
	/** From `/config.js`, served by the API at request time. */
	runtime?: string
	/** From `REACT_APP_CHAT_URL`, compiled into the bundle. */
	buildTime?: string
	/** `process.env.NODE_ENV`. */
	nodeEnv?: string
}

/** Preserved exactly, so a deployment that configures nothing is unaffected. */
export const DEV_CHAT_URL = 'http://localhost:3001'
export const PROD_CHAT_URL = 'https://chat.mydealteams.com'

/**
 * **Runtime beats build time, deliberately.**
 *
 * A compiled-in value cannot be corrected without rebuilding, and nothing
 * announces that it is wrong. The runtime one is the only answer a deploy can
 * fix, so it has to win where both exist.
 *
 * `REACT_APP_CHAT_URL` is kept rather than removed: it is still the only lever
 * for a build served by something that does not run our API, and dropping it
 * would break such a deployment silently.
 *
 * Whitespace-only values count as absent. A `.env` line like `CHAT_URL= ` is a
 * value nobody meant to set, and treating it as one would override a working
 * default with an origin the socket cannot reach.
 */
export const resolveChatServerUrl = ({
	runtime,
	buildTime,
	nodeEnv,
}: ChatServerUrlSources = {}): string => {
	const usable = (value?: string) =>
		typeof value === 'string' && value.trim() ? value.trim() : undefined

	return (
		usable(runtime) ??
		usable(buildTime) ??
		(nodeEnv === 'development' ? DEV_CHAT_URL : PROD_CHAT_URL)
	)
}

export default resolveChatServerUrl
